// @flow

// Looking for the top level of PinRep? This isn't quite it. Check out PinRepConfiguration.

// PinRepContainer is the parent to all Pin content and interaction. Pins can
// exist in many different variants based on their types (regular, recipe,
// promoted, video, etc), so we centralize configuration here. Unlike
// PinRepPresentation, PinRepContainer emits no markup and is purely meant to
// prepare content to place inside PinRepPresentation slots.

import React, { memo, useState, useRef, type Node } from 'react';
import ActionBar from 'app/common/react/components/PinRep/components/ActionBar/ActionBar';
import CarouselSliders from 'app/common/react/components/PinRep/components/CarouselSliders';
import Footer from 'app/common/react/components/PinRep/components/Footer/Footer';
import PinRepPrimaryContent from 'app/common/react/components/PinRep/components/PinRepPrimaryContent';
import PinTypeIdentifier from 'app/common/react/components/PinRep/components/PinTypeIdentifier';
import PinVisual from 'app/common/react/components/PinRep/components/PinVisual/PinVisual';
import SendShareFlyoutButtonFullWidth from 'app/common/react/components/PinRep/components/PinRepSendShareFlyoutButtonFullWidth';
import useHeightRestriction from 'app/common/react/components/PinRep/Hooks/useHeightRestriction';
import useHidingCarouselIndicator from 'app/common/react/components/PinRep/Hooks/useHidingCarouselIndicator';
import useHoverState from 'app/common/react/components/PinRep/Hooks/useHoverState';
import useTransitionManager from 'app/common/react/components/PinRep/Hooks/useTransitionManager';
import { Box } from 'gestalt';
import { type BuildActionBarPropsOutput } from 'app/common/react/components/PinRep/configUtils/buildActionBarProps';
import { type BuildFooterPropsOutput } from 'app/common/react/components/PinRep/configUtils/buildFooterProps';
import { type BuildPinVisualPropsOutput } from 'app/common/react/components/PinRep/configUtils/buildPinVisualProps';
import { type BuildPrimaryContentPropsOutput } from 'app/common/react/components/PinRep/configUtils/buildPrimaryContentProps';
import { type BuildSecondaryContentPropsOutput } from 'app/common/react/components/PinRep/configUtils/buildSecondaryContentProps';
import DebugPinImpressions, {
  type Props as DebugPinImpressionsProps,
} from 'app/common/react/components/PinRep/components/DebugPinImpressions';
import ObscuredOverlay, {
  type DerivedProps as ObscuredOverlayProps,
} from 'app/common/react/components/PinRep/components/ObscuredOverlay';
import PinRepPresentation, {
  type PresentationProps,
} from 'app/common/react/components/PinRep/core/PinRepPresentation';
import {
  getHighestVisibility,
  getLowestVisibility,
  getVisibilityFromTransitionState,
  reverseVisibility,
} from 'app/common/react/components/PinRep/components/ContentVisibility';
import { type Pin, type SelectionState } from 'app/common/react/components/PinRep/types';

const PIN_WIDTH = 228;

export type Props = {|
  pin: Pin,
  blockClickEvents?: boolean,
  // These props are derived in PinRepConfiguration
  currentlyPlayingVideo: boolean,
  // Primary & Secondary Content
  showPrimaryAndSecondaryContent: boolean,
  forcePrimaryContentVisibility: boolean,
  primaryContentProps: BuildPrimaryContentPropsOutput,
  secondaryContentProps: BuildSecondaryContentPropsOutput,
  // Accessory Content
  showAccessoryContent: boolean,
  // ActionBar
  showActionBar: boolean,
  actionBarProps: BuildActionBarPropsOutput,
  // Footer
  showFooter: boolean,
  footerProps: BuildFooterPropsOutput,
  showObscuredPinOverlay: boolean,
  obscuredPinOverlayProps?: ObscuredOverlayProps,
  pinVisualProps: BuildPinVisualPropsOutput,
  presentationProps: PresentationProps,
  showImpressionsDebugger: boolean,
  showSignalDebugger: boolean,
  impressionsDebuggerProps: DebugPinImpressionsProps,

  // Disable all hover events. This is useful for drag and drop.
  disableHover?: boolean,

  // Callbacks on interaction:
  onError?: () => void,
  onLoad?: () => void,

  // Does this Pin support selection? Is it selected?
  selectionState?: SelectionState,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

// PinRepContainer accepts _imperative_ props from PinRepConfiguration describing what elements are shown,
// and how those elements are shown. It handles showing/hiding those elements based on hover state.
// These elements are then passed down to PinRepPresentation, which handles the layout.
function PinRepContainer({
  actionBarProps,
  blockClickEvents,
  currentlyPlayingVideo,
  disableHover = false,
  footerProps,
  forcePrimaryContentVisibility,
  impressionsDebuggerProps,
  onError,
  onLoad,
  obscuredPinOverlayProps,
  pin,
  pinVisualProps,
  presentationProps,
  primaryContentProps,
  selectionState,
  secondaryContentProps,
  showAccessoryContent,
  showActionBar,
  showFooter,
  showImpressionsDebugger,
  showSignalDebugger,
  showObscuredPinOverlay,
  showPrimaryAndSecondaryContent,
  viewType,
}: Props) {
  // Derive Pin information from raw data.
  const { savedInfo } = pin;

  // There are some cases where we want to force the visibility of primary
  // content regardless of hover state. forcePrimary controls that, and
  // forcePrimaryTransitionState eventually gets merged with
  // hoverTransitionState to create primaryContentVisibility.
  const [forcePrimary, setForcePrimary] = useState<boolean>(false);
  const forcePrimaryTransitionState = useTransitionManager(forcePrimary);

  // Hovering is more than just a boolean because we may want to fade content in
  // and out as well as actually adding/removing it from the DOM. useHoverState
  // handles all state transitions and timing.
  let { isHovering, hoverTransitionState, handleMouseOver, handleMouseLeave } = useHoverState(); // eslint-disable-line prefer-const
  if (disableHover) {
    isHovering = false;
    hoverTransitionState = 'inactive';
  }

  // Override the visibility of some components based on the presence of
  // savedInfo.
  const savedTransitionState = useTransitionManager(!!savedInfo);
  const savedOverlayVisibility = getVisibilityFromTransitionState(savedTransitionState);

  const visualOverlayVisibility = getVisibilityFromTransitionState(hoverTransitionState);

  // Based on data, experiments, hover, etc, figure out what state of visibility
  // our content exists in. These may get overridden below.
  const hoverVisibility = getVisibilityFromTransitionState(hoverTransitionState);
  const forcePrimaryVisibility = getVisibilityFromTransitionState(forcePrimaryTransitionState);
  const savedVisibility = getVisibilityFromTransitionState(savedTransitionState);

  const itemVisibility = {
    primaryContentVisibility: getHighestVisibility(
      hoverVisibility,
      forcePrimaryVisibility,
      savedVisibility,
    ),
    secondaryContentVisibility: reverseVisibility(
      getHighestVisibility(hoverVisibility, forcePrimaryVisibility, savedVisibility),
    ),
    actionBarVisibility: getHighestVisibility(hoverVisibility, forcePrimaryVisibility),
    accessoryContentVisibility: 'none',
  };

  const onToggleFlyout = (showToggle: boolean) =>
    // If showToggle is true, set force the Pin to show primary content.
    setForcePrimary(showToggle);
  const onToggleModal = (showModal: boolean) => {
    // We no longer need to force primary.
    setForcePrimary(showModal);

    // Since hover isn't cleared properly when the modal opens/closes,
    // manually stop hover here.
    if (!showModal) {
      handleMouseLeave();
    }
  };

  // Build primary content.
  let primaryContent: Node = null;

  // in some instances, ie: more ideas surface, we want the button to be persistent
  if (forcePrimaryContentVisibility) {
    itemVisibility.primaryContentVisibility = 'visible';
  }

  if (itemVisibility.primaryContentVisibility !== 'none' && showPrimaryAndSecondaryContent) {
    primaryContent = (
      <PinRepPrimaryContent
        pin={pin}
        onToggleFlyout={onToggleFlyout}
        onToggleModal={onToggleModal}
        {...primaryContentProps}
      />
    );
  }

  // Build secondary content.
  let secondaryContent: Node = null;

  // Secondary content may be hidden on a timer. Handle this case.
  const isFirstRenderRef = useRef(true);
  const isFirstRender = isFirstRenderRef.current;
  isFirstRenderRef.current = false;

  const showSecondaryContent = useHidingCarouselIndicator(pin, isFirstRender);
  const secondaryContentVisibleTransitionState = useTransitionManager(showSecondaryContent);
  const secondaryContentVisibility = getVisibilityFromTransitionState(
    secondaryContentVisibleTransitionState,
  );

  itemVisibility.secondaryContentVisibility = getLowestVisibility(
    itemVisibility.secondaryContentVisibility,
    secondaryContentVisibility,
  );

  // Price identifier of a carousel product pin should not depend on transition state.
  secondaryContent = (secondaryContentProps.allowShoppingPriceIndicator ||
    itemVisibility.secondaryContentVisibility !== 'none') &&
    showPrimaryAndSecondaryContent &&
    !currentlyPlayingVideo && (
      <Box margin={2}>
        <PinTypeIdentifier pin={pin} {...secondaryContentProps} />
      </Box>
    );

  // Build accessory content.
  let accessoryContent: Node = null;

  if (showAccessoryContent) {
    accessoryContent = <CarouselSliders pin={pin} />;
    itemVisibility.accessoryContentVisibility = getVisibilityFromTransitionState(
      hoverTransitionState,
    );
  }

  // Build action bar content.
  if (pinVisualProps.isSocialSeasonalBoard) {
    actionBarProps.showShareButton = false;
  }

  const actionBarContent = showActionBar ? (
    <ActionBar
      onToggleFlyout={onToggleFlyout}
      onToggleModal={onToggleModal}
      pin={pin}
      setForcePrimary={setForcePrimary}
      {...actionBarProps}
    />
  ) : null;

  // Build the footer (description, attribution, etc).
  let footerContent = null;

  if (showFooter) {
    footerContent = (
      <Footer
        blockClickEvents={blockClickEvents}
        hoverTransitionState={
          // This avoids unnecessary re-renders when Footer doesn't use hoverTransitionState
          footerProps.attributionType === 'image-only-credit' ? hoverTransitionState : null
        }
        pin={pin}
        setForcePrimary={setForcePrimary}
        {...footerProps}
      />
    );
  }

  if (pinVisualProps.isSocialSeasonalBoard) {
    footerContent = (
      <Box data-test-id="social-seasonal-footer" marginTop={2} marginBottom={3} width="100%">
        <Box margin="auto" width={PIN_WIDTH}>
          <SendShareFlyoutButtonFullWidth
            isFlyoutOpen={forcePrimary}
            onFlyoutDismiss={() => onToggleFlyout(!forcePrimary)}
            onToggleFlyout={() => onToggleFlyout(!forcePrimary)}
            pinId={pin.id}
            sendType="pin"
            shownWithinMasonry
            viewType={viewType}
          />
        </Box>
      </Box>
    );
  }

  // Build debug content.
  const debugContent = showImpressionsDebugger && (
    <DebugPinImpressions {...impressionsDebuggerProps} />
  );

  // Build signal debugger content.
  const signalDebuggerContent = showSignalDebugger &&
    pin.debug_info_html && (
      <Box paddingY={1}>
        <pre>{pin.debug_info_html}</pre>
      </Box>
    );

  // Handle elephant Pin progressive degredation. Elephant pins don't have room
  // to show content and controls over the top of the image, so we force those
  // to be hidden. If the user wants to interact, she can click into closeup.
  const visualContainerRef = useRef();
  const heightRestriction = useHeightRestriction(visualContainerRef);

  if (heightRestriction === 'header-only') {
    itemVisibility.actionBarVisibility = 'none';
    itemVisibility.accessoryContentVisibility = 'none';
  }

  const obscuredPinOverlay = showObscuredPinOverlay ? (
    <ObscuredOverlay pin={pin} {...obscuredPinOverlayProps} />
  ) : null;

  return (
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    <div
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      data-test-id="pin"
      data-test-pin-id={pin.id}
    >
      <PinRepPresentation
        visualOverlayVisibility={visualOverlayVisibility}
        savedOverlayVisibility={savedOverlayVisibility}
        primaryContent={primaryContent}
        primaryContentVisibility={itemVisibility.primaryContentVisibility}
        secondaryContent={secondaryContent}
        secondaryContentVisibility={itemVisibility.secondaryContentVisibility}
        accessoryContent={accessoryContent}
        accessoryContentVisibility={itemVisibility.accessoryContentVisibility}
        actionBar={actionBarContent}
        actionBarVisibility={itemVisibility.actionBarVisibility}
        footer={footerContent}
        selectionState={selectionState}
        debugContent={debugContent}
        signalDebuggerContent={signalDebuggerContent}
        obscuredPinOverlay={obscuredPinOverlay}
        {...presentationProps}
      >
        <Box ref={visualContainerRef}>
          <PinVisual
            blockClickEvents={blockClickEvents}
            isHovering={isHovering}
            onError={onError}
            onLoad={onLoad}
            pin={pin}
            {...pinVisualProps}
          />
        </Box>
      </PinRepPresentation>
    </div>
  );
}

export default memo<Props>(PinRepContainer);
